import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { AccountContext } from '../../../../components/AccountContext';
import { NotificationContext } from '../../../../components/GrowlNotifications';
import {
  ADD_SUPER_PROFILE_MUTATION,
  UPDATE_SUPER_PROFILE_MUTATION,
} from '../services/super--profiles.service';
import { growlActionSuccess, growlErrors } from '../../../../utils/NotificationUtil';
import { useMutation } from '@apollo/react-hooks';
import { notificationMessages } from '../../../../i18n/common';
import { useIntl } from 'react-intl';

const useSuperProfile = () => {
  const { formatMessage } = useIntl();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const history = useHistory();

  const [addSuperProfileMutation] = useMutation(ADD_SUPER_PROFILE_MUTATION, {
    refetchQueries: ['superProfilesQuery'],
  });
  const [updateSuperProfileMutation] = useMutation(UPDATE_SUPER_PROFILE_MUTATION, {
    refetchQueries: ['superProfilesQuery'],
  });

  const handleAddSuperProfile = async (values) => {
    try {
      await addSuperProfileMutation({
        variables: {
          input: {
            accountId: currentAccount,
            jobProfileId: values.jobProfileId,
            superProfileInformation: {
              internalName: values.internalName,
              publicTitle: values.publicTitle,
              publicDescription: values.publicDescription,
              occupationalLevel: values.occupationalLevel,
              workCategory: values.workCategory,
              workArea: values.workArea,
              workplaceDetails: {
                type: values.workplaceDetailsType,
                officeOnlyWorkplaceDetailsData: {
                  country: values.workplaceDetailsOfficeOnlyCountry,
                  regions: values.workplaceDetailsOfficeOnlyRegions,
                },
                officeAndRemoteWorkplaceDetailsData: {
                  country: values.workplaceDetailsOfficeAndRemoteCountry,
                  regions: values.workplaceDetailsOfficeAndRemoteRegions,
                },
                remoteOnlyWorkplaceDetailsData: {
                  fromAnyCountry: values.workplaceDetailsRemoteOnlyFromAnyCountry,
                  countries: values.workplaceDetailsRemoteOnlyCountries,
                },
              },
              requiredMinimumEducation: values.requiredMinimumEducation,
              educationRelevance: values.educationRelevance,
              requiredJobSeniority: values.requiredJobSeniority,
              jobSeniorityRelevance: values.jobSeniorityRelevance,
              requiredManagementExperience: values.requiredManagementExperience,
              requiredLanguageProficiencies: values.requiredLanguageProficiencies.map((requiredLanguageProficiency) => {
                return {
                  language: requiredLanguageProficiency.language,
                  proficiency: requiredLanguageProficiency.proficiency,
                };
              }),
              minimumOverallAssessmentScoreRequired: values.minimumOverallAssessmentScoreRequired,
              minimumOverallQAndRScoreRequired: values.minimumOverallQAndRScoreRequired,
            },
          },
        },
      });

      growlActionSuccess(growl, formatMessage(notificationMessages.success));
      history.push(`/accounts/${currentAccount}/super-profiles`);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  const updateSuperProfileDetails = async (values, superProfileId) => {
    const workplaceDetailsType = values.workplaceDetailsType;
    const officeOnlyWorkplaceDetailsData =
      workplaceDetailsType === 'OFFICE_ONLY'
        ? {
            country: values.workplaceDetailsOfficeOnlyCountry,
            regions: values.workplaceDetailsOfficeOnlyRegions,
          }
        : null;
    const officeAndRemoteWorkplaceDetailsData =
      workplaceDetailsType === 'OFFICE_AND_REMOTE'
        ? {
            country: values.workplaceDetailsOfficeAndRemoteCountry,
            regions: values.workplaceDetailsOfficeAndRemoteRegions,
          }
        : null;
    const remoteOnlyWorkplaceDetailsData =
      workplaceDetailsType === 'REMOTE_ONLY'
        ? {
            fromAnyCountry: values.workplaceDetailsRemoteOnlyFromAnyCountry || false,
            countries: values.workplaceDetailsRemoteOnlyCountries,
          }
        : null;

    try {
      await updateSuperProfileMutation({
        variables: {
          input: {
            accountId: currentAccount,
            superProfileId,
            jobProfileId: values.jobProfileId,
            superProfileInformation: {
              internalName: values.internalName,
              publicTitle: values.publicTitle,
              publicDescription: values.publicDescription,
              occupationalLevel: values.occupationalLevel,
              workCategory: values.workCategory,
              workArea: values.workArea,
              workplaceDetails: {
                type: workplaceDetailsType,
                officeOnlyWorkplaceDetailsData,
                officeAndRemoteWorkplaceDetailsData,
                remoteOnlyWorkplaceDetailsData,
              },
              requiredMinimumEducation: values.requiredMinimumEducation,
              educationRelevance: values.educationRelevance,
              requiredJobSeniority: values.requiredJobSeniority,
              jobSeniorityRelevance: values.jobSeniorityRelevance,
              requiredManagementExperience: values.requiredManagementExperience,
              requiredLanguageProficiencies: values.requiredLanguageProficiencies.map((requiredLanguageProficiency) => {
                return {
                  language: requiredLanguageProficiency.language,
                  proficiency: requiredLanguageProficiency.proficiency,
                };
              }),
              minimumOverallAssessmentScoreRequired: values.minimumOverallAssessmentScoreRequired,
              minimumOverallQAndRScoreRequired: values.minimumOverallQAndRScoreRequired,
            },
          },
        },
      });

      growlActionSuccess(growl, formatMessage(notificationMessages.success));
      history.push(`/accounts/${currentAccount}/super-profiles`);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  return {
    handleAddSuperProfile,
    updateSuperProfileDetails,
  };
};

export default useSuperProfile;
